/**
 * useLandingSettings — page-wide settings for the public landing page.
 *
 * Fetches `/landing/settings/public/` to learn:
 *   - hero_mode               'slider' | 'video'
 *   - language_switch_enabled boolean  (drives navbar EN/AR toggle visibility)
 *   - hero_video_url          string   (used when hero_mode === 'video')
 *   - hero_video_poster_url   string   (poster image for the video)
 *
 * On API failure, returns defaults that match the current behaviour
 * (slider mode, language switch on, no video URLs).  The public page
 * therefore renders correctly even if the CMS endpoint is unreachable.
 *
 * Cache: localStorage with a 5-minute TTL.  Settings change rarely; this
 * avoids a network request on every page load.
 */

import { useEffect, useState } from 'react'
import apiConfig from '../../../config/apiConfig'

const CACHE_KEY = 'ovacs.landingSettings.v1'
const CACHE_TTL_MS = 5 * 60 * 1000

const DEFAULTS = {
  hero_mode: 'slider',
  language_switch_enabled: true,
  hero_video_url: '',
  hero_video_poster_url: '',
}

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null
    return parsed.settings
  } catch { return null }
}
function writeCache(settings) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), settings }))
  } catch { /* ignore */ }
}

export function useLandingSettings() {
  const [settings, setSettings] = useState(() => readCache() || DEFAULTS)
  const [loaded, setLoaded] = useState(() => Boolean(readCache()))

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${apiConfig.API_BASE_URL}/landing/settings/public/`)
        if (!res.ok) return
        const body = await res.json()
        const data = body?.data || {}
        if (cancelled) return
        const next = {
          hero_mode: data.hero_mode === 'video' ? 'video' : 'slider',
          language_switch_enabled: data.language_switch_enabled !== false,
          hero_video_url: data.hero_video_url || '',
          hero_video_poster_url: data.hero_video_poster_url || '',
        }
        setSettings(next)
        writeCache(next)
      } catch {
        // Network error — leave defaults / cache in place.
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { settings, loaded }
}
